import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import pluralize from 'pluralize';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FlowBuilderBlockTypes,
  ParticipantsBuilderBlockData,
  VisibilityBuilderBlockData,
} from '../../../../interfaces/Flow/Builder';
import {
  LOADING_LABEL,
  PARTICIPANT,
} from '../../../../languages/en/singleWords';
import useFlowBuilderStore from '../../../../stores/flowBuilderStore';
import { setSpecificBlockDataSelector } from '../../../../stores/flowBuilderStore/selectors';
import { SHARE_SHEET_EVENTS } from '../../../../Utils/analytics/constants';
import { trackShareSheetActionEvent } from '../../../../Utils/analytics/shareSheet';
import { mapRulesFromCriteriaGroups } from '../../../../Utils/flows/criteria';
import { serializeVisibilityBlock } from '../../../../Utils/flows/builder/utils';
import { ShareSheetModalScreen } from '../types';
import { useFetchMembersBasedOnFlowCriteria } from './useFetchMembersBasedOnFlowCriteria';

export const useFlowsShareSheetRules = ({
  updateModalCurrentState,
  setHasModifiedChanges,
}: {
  updateModalCurrentState: (currentState: ShareSheetModalScreen) => void;
  setHasModifiedChanges: (hasDataChanged: boolean) => void;
}) => {
  const setSpecificBlockData = useFlowBuilderStore(
    setSpecificBlockDataSelector,
  );

  const [currentVisibilityBlockData, setCurrentVisibilityBlockData] =
    useState<VisibilityBuilderBlockData | null>(null);

  const [visibilityTitleContent, setVisibilityTitleContent] = useState('');

  const [currentParticipantsBlockData, setCurrentParticipantsBlockData] =
    useState<ParticipantsBuilderBlockData>();

  const {
    isParticipantsCriteriaLoading,
    totalParticipantsCount,
    participantsCriteria,
    participantsBlockData,
    isViewerCriteriaLoading,
    totalViewersCount,
    visibilityBlockData,
  } = useFetchMembersBasedOnFlowCriteria({
    enableFetch: true,
    isMembersOnly: false,
  });

  useEffect(() => {
    setCurrentParticipantsBlockData(cloneDeep(participantsBlockData));
  }, [participantsBlockData]);

  useEffect(() => {
    setCurrentVisibilityBlockData(cloneDeep(visibilityBlockData));
  }, [visibilityBlockData]);

  const updateParticipantsBlockData = useCallback(
    (updatedContentBlock: ParticipantsBuilderBlockData) =>
      setCurrentParticipantsBlockData(updatedContentBlock),
    [],
  );

  const participantsCount = useMemo(() => {
    if (
      !isEmpty(participantsCriteria) &&
      !isEmpty(participantsCriteria?.groups)
    ) {
      return totalParticipantsCount;
    }

    return 0;
  }, [participantsCriteria, totalParticipantsCount]);

  const visibilityCount = useMemo(() => {
    if (!isViewerCriteriaLoading) {
      return totalViewersCount;
    }

    return 1;
  }, [totalViewersCount, isViewerCriteriaLoading]);

  const subtitleContent = useMemo(() => {
    if (isParticipantsCriteriaLoading) {
      return LOADING_LABEL;
    }

    return participantsCount > 0
      ? `${participantsCount} ${pluralize(
          PARTICIPANT,
          participantsCount,
          false,
        )}`
      : `0 ${PARTICIPANT}`;
  }, [isParticipantsCriteriaLoading, participantsCount]);

  const updateCurrentVisibilityBlockData = useCallback(
    (updatedContentBlock: VisibilityBuilderBlockData) =>
      setCurrentVisibilityBlockData(updatedContentBlock),
    [],
  );

  const handleChooseParticipants = useCallback(() => {
    updateModalCurrentState(ShareSheetModalScreen.PARTICIPANTS);
  }, [updateModalCurrentState]);

  const handleChooseViewers = useCallback(() => {
    updateModalCurrentState(ShareSheetModalScreen.VIEWERS);
  }, [updateModalCurrentState]);

  const handleVisibilityConfirmButtonClick = useCallback(() => {
    if (currentVisibilityBlockData) {
      trackShareSheetActionEvent({
        tab: 'invite and share',
        action: SHARE_SHEET_EVENTS.VIEWERS_SELECTED,
        modalVariant: 'choose viewers',
        viewerCriteriaSelected: serializeVisibilityBlock(visibilityBlockData),
      });
      setSpecificBlockData(
        FlowBuilderBlockTypes.VISIBILITY,
        currentVisibilityBlockData,
      );
    }

    updateModalCurrentState(ShareSheetModalScreen.HOME);
    setHasModifiedChanges(true);
  }, [
    currentVisibilityBlockData,
    setHasModifiedChanges,
    setSpecificBlockData,
    updateModalCurrentState,
    visibilityBlockData,
  ]);

  const handleVisibilityBackButtonClick = useCallback(() => {
    if (visibilityBlockData)
      updateCurrentVisibilityBlockData(cloneDeep(visibilityBlockData));
    updateModalCurrentState(ShareSheetModalScreen.HOME);
  }, [
    updateCurrentVisibilityBlockData,
    updateModalCurrentState,
    visibilityBlockData,
  ]);

  const handleBackButtonClick = useCallback(() => {
    setCurrentParticipantsBlockData(cloneDeep(participantsBlockData));
    updateModalCurrentState(ShareSheetModalScreen.HOME);
  }, [participantsBlockData, updateModalCurrentState]);

  const handleConfirmButtonClick = useCallback(() => {
    if (currentParticipantsBlockData) {
      trackShareSheetActionEvent({
        tab: 'invite and share',
        action: SHARE_SHEET_EVENTS.PARTICIPANTS_SELECTED,
        modalVariant: 'choose participants',
        participantCriteriaSelected: mapRulesFromCriteriaGroups(
          participantsBlockData.participantsCriteria,
          undefined,
          false,
        ),
      });
      setSpecificBlockData(
        FlowBuilderBlockTypes.PARTICIPANTS,
        currentParticipantsBlockData,
      );
    }

    updateModalCurrentState(ShareSheetModalScreen.HOME);
    setHasModifiedChanges(true);
  }, [
    currentParticipantsBlockData,
    participantsBlockData.participantsCriteria,
    setHasModifiedChanges,
    setSpecificBlockData,
    updateModalCurrentState,
  ]);

  return {
    handleChooseParticipants,
    handleChooseViewers,
    participantsCriteria,
    isViewerCriteriaLoading,
    visibilityCount,
    subtitleContent,
    visibilityTitleContent,
    setVisibilityTitleContent,
    updateCurrentVisibilityBlockData,
    updateParticipantsBlockData,
    currentParticipantsBlockData,
    handleVisibilityConfirmButtonClick,
    handleBackButtonClick,
    handleVisibilityBackButtonClick,
    visibilityBlockData,
    participantsBlockData,
    participantsCount,
    currentVisibilityBlockData,
    setSpecificBlockData,
    handleConfirmButtonClick,
  };
};
